import type { TalentDiscoveryByIdUserV3 } from '@revelio/data-access';

import type { TableRow } from '../../TableSelectionProvider/TableSelectionProvider';
import type {
  EducationEntry,
  FullProfileInfoData,
  LanguageEntry,
  ProfileHeader,
  ProfileHeaderOriginalValues,
  ProfileMetadata,
  PublicationEntry,
  WorkExperienceEntry,
} from './types';

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const formatDateToMonthYear = (
  dateString: string | number | null | undefined
): string => {
  if (!dateString || dateString === '') {
    return '';
  }

  const dateStr = String(dateString);
  const [year, month] = dateStr.split('-');

  if (!year || !month) {
    return dateStr;
  }

  const monthIndex = Number(month);
  // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
  if (isNaN(monthIndex) || monthIndex < 1 || monthIndex > 12) {
    return dateStr;
  }

  return `${monthNames[monthIndex - 1]} ${year}`;
};

const formatDateToYear = (
  dateString: string | number | null | undefined
): string => {
  if (!dateString || dateString === '') {
    return '';
  }

  const dateStr = String(dateString);
  const [year] = dateStr.split('-');

  return year || dateStr;
};

const parseUserId = (value: unknown): string | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }
  return undefined;
};

const getValue = (
  row: TableRow,
  field: string,
  fallback: string | number | undefined
): string | number => {
  const value = row[field];
  if (value === null || value === undefined || value === '') {
    return fallback ?? '';
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }
  return fallback ?? '';
};

const getStringValue = (
  row: TableRow,
  field: string,
  fallback: string
): string => {
  return String(getValue(row, field, fallback));
};

const getNumberValue = (
  row: TableRow,
  field: string,
  fallback: number
): number => {
  const value = getValue(row, field, fallback);
  if (typeof value === 'number') {
    return value;
  }
  const parsed = Number(value);
  // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
  return isNaN(parsed) ? fallback : parsed;
};

const transformWorkHistoryEntry = (
  entry: Record<string, unknown>
): WorkExperienceEntry => {
  const startDate = entry.start_date
    ? formatDateToMonthYear(String(entry.start_date))
    : '';

  let endDate = entry.end_date
    ? formatDateToMonthYear(String(entry.end_date))
    : '';
  if (!endDate && startDate) {
    endDate = 'Present';
  }
  const role = entry.current_title ? String(entry.current_title) : '';
  const company = entry.current_company ? String(entry.current_company) : '';
  const location = entry.location ? String(entry.location) : '';
  const positionLocation = [company, location].filter(Boolean).join(', ');
  const description = entry.description ? String(entry.description) : '';
  const descriptionFormatted =
    typeof entry.description_formatted === 'boolean'
      ? entry.description_formatted
      : false;
  const salaryBase =
    typeof entry.salary_base === 'number'
      ? entry.salary_base
      : Number(entry.salary_base) || 0;

  let salaryTotal = 0;
  if (typeof entry.salary_total === 'number') {
    salaryTotal = entry.salary_total;
  } else if (typeof entry.salary_max === 'number') {
    salaryTotal = entry.salary_max;
  } else {
    salaryTotal = Number(entry.salary_total || entry.salary_max) || 0;
  }

  const salaryMin = Math.min(salaryBase, salaryTotal);
  const salaryMax = Math.max(salaryBase, salaryTotal);

  const jobFamily = entry.job_family ? String(entry.job_family) : '';
  const seniority = entry.seniority ? String(entry.seniority) : '';
  const companyPrestige = entry.prestige ? String(entry.prestige) : '';
  const tenure = entry.tenure ? String(entry.tenure) : '';
  const skills = Array.isArray(entry.skills) ? entry.skills.map(String) : [];

  return {
    startDate,
    endDate,
    role,
    positionLocation,
    description,
    isFormatted: descriptionFormatted,
    jobFamily,
    seniority,
    companyPrestige,
    tenure,
    salary: {
      min: salaryMin,
      max: salaryMax,
    },
    skills,
  };
};

const transformEducationEntry = (
  entry: Record<string, unknown>
): EducationEntry => {
  const startDate = entry.start_date
    ? formatDateToYear(String(entry.start_date))
    : '';
  const endDate = entry.end_date
    ? formatDateToYear(String(entry.end_date))
    : '';
  const institution = entry.university_name
    ? String(entry.university_name)
    : '';
  const degree = entry.title ? String(entry.title) : '';
  const fieldOfStudy = entry.field_of_study ? String(entry.field_of_study) : '';
  const description = entry.description ? String(entry.description) : '';

  return {
    startDate,
    endDate,
    institution,
    degree,
    fieldOfStudy: fieldOfStudy || undefined,
    description: description || undefined,
  };
};

const transformPublicationEntry = (
  entry: Record<string, unknown>
): PublicationEntry => {
  const date =
    entry.publication_date || entry.date
      ? formatDateToMonthYear(String(entry.publication_date || entry.date))
      : '';
  const title = entry.title ? String(entry.title) : '';
  const source =
    entry.organization || entry.source
      ? String(entry.organization || entry.source)
      : '';
  const description = entry.description ? String(entry.description) : '';

  return {
    date,
    title,
    source,
    description,
  };
};

const transformLanguageEntry = (
  entry: Record<string, unknown>
): LanguageEntry => {
  const language = entry.language ? String(entry.language) : '';
  const proficiency = entry.proficiency ? String(entry.proficiency) : '';

  return {
    language,
    proficiency,
  };
};

const createEmptyProfileData = (): FullProfileInfoData => ({
  userId: undefined,
  originalUserId: undefined,
  header: {
    name: '',
    role: '',
    phone: '',
    workEmail: '',
    location: '',
    personalEmail: '',
    profileUrl: '',
  },
  metadata: {
    salary: {
      min: 0,
      max: 0,
    },
    prestige: '',
    flightRisk: '',
  },
  workExperience: [],
  education: [],
  skills: [],
  publications: [],
  languages: [],
});

export const transformTableRowToProfileData = (
  row: TableRow | undefined
): FullProfileInfoData => {
  if (!row) {
    return createEmptyProfileData();
  }

  const userId = parseUserId(row.user_id);
  const originalUserId = parseUserId(row.original_user_id);
  const firstName =
    getStringValue(row, 'first_name', '') ||
    getStringValue(row, 'First Name', '');
  const lastName =
    getStringValue(row, 'last_name', '') ||
    getStringValue(row, 'Last Name', '');
  const fullName =
    firstName && lastName
      ? `${firstName} ${lastName}`
      : firstName || lastName || '';
  const originalValues = row.__originalValues ?? {};
  const maskedValues = row.__maskedValues ?? {};
  const headerOriginalValues: ProfileHeaderOriginalValues = {
    lastName: originalValues['last_name'],
    workEmail: originalValues['email'],
    personalEmail: originalValues['personal_email'],
    phone: originalValues['phone_number'],
    profileUrl: originalValues['linkedin_url'],
  };
  const hasOriginalValues = Object.values(headerOriginalValues).some((value) =>
    Boolean(value)
  );
  const headerMaskedValues: ProfileHeaderOriginalValues = {
    lastName: maskedValues['last_name'],
    workEmail: maskedValues['email'],
    personalEmail: maskedValues['personal_email'],
    phone: maskedValues['phone_number'],
    profileUrl: maskedValues['linkedin_url'],
  };
  const hasMaskedValues = Object.values(headerMaskedValues).some((value) =>
    Boolean(value)
  );

  const header: ProfileHeader = {
    name: fullName,
    firstName,
    lastName,
    role:
      getStringValue(row, 'current_title', '') ||
      getStringValue(row, 'Current Title', ''),
    phone:
      getStringValue(row, 'phone_number', '') ||
      getStringValue(row, 'Phone Number', ''),
    workEmail:
      getStringValue(row, 'email', '') ||
      getStringValue(row, 'Professional Email', ''),
    location:
      getStringValue(row, 'user_location', '') ||
      getStringValue(row, 'location', '') ||
      getStringValue(row, 'Location', ''),
    personalEmail:
      getStringValue(row, 'personal_email', '') ||
      getStringValue(row, 'Personal Email', ''),
    profileUrl:
      getStringValue(row, 'linkedin_url', '') ||
      getStringValue(row, 'Profile URL', '') ||
      getStringValue(row, 'LinkedIn URL', ''),
    originalValues: hasOriginalValues ? headerOriginalValues : undefined,
    maskedValues: hasMaskedValues ? headerMaskedValues : undefined,
  };

  const totalCompensation =
    getNumberValue(row, 'salary_total', 0) ||
    getNumberValue(row, 'Total Compensation', 0);
  const baseSalary =
    getNumberValue(row, 'salary_base', 0) ||
    getNumberValue(row, 'Base Salary', 0);

  const salaryMin = Math.min(baseSalary, totalCompensation);
  const salaryMax = Math.max(baseSalary, totalCompensation);

  const metadata: ProfileMetadata = {
    salary: {
      min: salaryMin,
      max: salaryMax,
    },
    prestige:
      getStringValue(row, 'prestige', '') ||
      getStringValue(row, 'Prestige', ''),
    flightRisk:
      getStringValue(row, 'flight_risk', '') ||
      getStringValue(row, 'Flight Risk', ''),
  };

  const workHistory = (row.work_history as unknown[]) || [];
  const workExperience: WorkExperienceEntry[] =
    workHistory.length > 0
      ? workHistory.map((entry) =>
          transformWorkHistoryEntry(entry as Record<string, unknown>)
        )
      : [];

  const educationHistory = (row.education_history as unknown[]) || [];
  const education: EducationEntry[] =
    educationHistory.length > 0
      ? educationHistory.map((entry) =>
          transformEducationEntry(entry as Record<string, unknown>)
        )
      : [];

  const topLevelSkills = Array.isArray(row.skills)
    ? row.skills
        .map((skill) => {
          if (typeof skill === 'string') return skill.trim();
          if (typeof skill === 'number') return String(skill);
          return null;
        })
        .filter((skill): skill is string => skill !== null && skill.length > 0)
    : [];
  const workExperienceSkills = workExperience.flatMap(
    (entry) => entry.skills || []
  );
  const allSkills = [
    ...new Set([
      ...topLevelSkills,
      ...workExperienceSkills.filter(
        (skill): skill is string =>
          typeof skill === 'string' && skill.trim().length > 0
      ),
    ]),
  ];

  const publicationsHistory =
    (row.publications as unknown[]) ||
    (row.publications_history as unknown[]) ||
    [];
  const publications: PublicationEntry[] =
    publicationsHistory.length > 0
      ? publicationsHistory.map((entry) =>
          transformPublicationEntry(entry as Record<string, unknown>)
        )
      : [];

  const languagesHistory =
    (row.languages as unknown[]) ||
    (row.language_proficiencies as unknown[]) ||
    [];
  const languages: LanguageEntry[] =
    languagesHistory.length > 0
      ? languagesHistory.map((entry) =>
          transformLanguageEntry(entry as Record<string, unknown>)
        )
      : [];

  return {
    userId,
    originalUserId,
    header,
    metadata,
    workExperience,
    education,
    skills: allSkills,
    publications,
    languages,
  };
};

export const transformByIdUserToProfileData = (
  user: TalentDiscoveryByIdUserV3 | null | undefined
): FullProfileInfoData => {
  if (!user) {
    return createEmptyProfileData();
  }

  const userId = String(user.user_id);
  const originalUserId = String(user.original_user_id);
  const firstName = user.first_name ?? '';
  const lastName = user.last_name ?? '';
  const name = [firstName, lastName]
    .map((value) => value.trim())
    .filter((value) => value.length > 0)
    .join(' ');

  const workHistory = Array.isArray(user.work_history) ? user.work_history : [];
  const educationHistory = Array.isArray(user.education_history)
    ? user.education_history
    : [];
  const primaryWorkHistory = workHistory[0];

  const header: ProfileHeader = {
    name,
    firstName,
    lastName,
    role: primaryWorkHistory?.current_title ?? '',
    phone: primaryWorkHistory?.phone_number ?? '',
    workEmail: primaryWorkHistory?.email ?? '',
    location: user.user_location || primaryWorkHistory?.location || '',
    personalEmail: primaryWorkHistory?.personal_email ?? '',
    profileUrl: primaryWorkHistory?.linkedin_url ?? '',
  };

  const baseSalary =
    typeof primaryWorkHistory?.salary_base === 'number'
      ? primaryWorkHistory.salary_base
      : 0;
  const totalSalary =
    typeof primaryWorkHistory?.salary_total === 'number'
      ? primaryWorkHistory.salary_total
      : 0;
  const salaryMin = Math.min(baseSalary, totalSalary);
  const salaryMax = Math.max(baseSalary, totalSalary);

  const metadata: ProfileMetadata = {
    salary: {
      min: salaryMin,
      max: salaryMax,
    },
    prestige: primaryWorkHistory?.prestige ?? '',
    flightRisk: primaryWorkHistory?.flight_risk ?? '',
  };

  const workExperience: WorkExperienceEntry[] =
    workHistory.length > 0
      ? workHistory.map((entry) =>
          transformWorkHistoryEntry(entry as Record<string, unknown>)
        )
      : [];

  let education: EducationEntry[] = [];
  if (educationHistory.length > 0) {
    education = educationHistory.map((entry) =>
      transformEducationEntry(entry as Record<string, unknown>)
    );
  } else if (user.education) {
    education = [
      {
        startDate: '',
        endDate: user.graduation_year
          ? formatDateToYear(String(user.graduation_year))
          : '',
        institution: user.education,
        degree: '',
        fieldOfStudy: undefined,
        description: undefined,
      },
    ];
  }

  const skills = workHistory
    .flatMap((entry) => (Array.isArray(entry.skills) ? entry.skills : []))
    .filter((skill): skill is string => typeof skill === 'string')
    .map((skill) => skill.trim())
    .filter((skill) => skill.length > 0);
  const uniqueSkills = Array.from(new Set(skills));

  return {
    userId,
    originalUserId,
    header,
    metadata,
    workExperience,
    education,
    skills: uniqueSkills,
    publications: [],
    languages: [],
  };
};

export const enrichProfileDataWithDefaults = (
  profileData: FullProfileInfoData,
  mockData: FullProfileInfoData
): FullProfileInfoData => {
  const header = {
    name: profileData.header.name || mockData.header.name,
    firstName: profileData.header.firstName || mockData.header.firstName,
    lastName: profileData.header.lastName || mockData.header.lastName,
    role: profileData.header.role || mockData.header.role,
    phone: profileData.header.phone || mockData.header.phone,
    workEmail: profileData.header.workEmail || mockData.header.workEmail,
    location: profileData.header.location || mockData.header.location,
    personalEmail:
      profileData.header.personalEmail || mockData.header.personalEmail,
    profileUrl: profileData.header.profileUrl || mockData.header.profileUrl,
    originalValues: profileData.header.originalValues,
    maskedValues: profileData.header.maskedValues,
  };

  const metadata = {
    salary: {
      min: profileData.metadata.salary.min || mockData.metadata.salary.min,
      max: profileData.metadata.salary.max || mockData.metadata.salary.max,
    },
    prestige: profileData.metadata.prestige || mockData.metadata.prestige,
    flightRisk: profileData.metadata.flightRisk || mockData.metadata.flightRisk,
  };

  const workExperience: WorkExperienceEntry[] =
    profileData.workExperience && profileData.workExperience.length > 0
      ? profileData.workExperience.map((entry, index) => {
          const mockEntry =
            mockData.workExperience[index] ??
            (mockData.workExperience[0] as WorkExperienceEntry);

          // Handle endDate special case: if mockEntry has 'Present', preserve it
          let endDate = entry.endDate || mockEntry.endDate;
          if (mockEntry.endDate === 'Present' && entry.endDate) {
            endDate = 'Present';
          }

          return {
            startDate: entry.startDate || mockEntry.startDate,
            endDate,
            role: entry.role || mockEntry.role,
            positionLocation:
              entry.positionLocation || mockEntry.positionLocation,
            description: entry.description || mockEntry.description,
            isFormatted: entry.isFormatted ?? false,
            jobFamily: entry.jobFamily || mockEntry.jobFamily,
            seniority: entry.seniority || mockEntry.seniority,
            companyPrestige: entry.companyPrestige || mockEntry.companyPrestige,
            tenure: entry.tenure || mockEntry.tenure,
            salary: (() => {
              const entryMin =
                entry.salary.min && entry.salary.min > 0
                  ? entry.salary.min
                  : mockEntry.salary.min;
              const entryMax =
                entry.salary.max && entry.salary.max > 0
                  ? entry.salary.max
                  : mockEntry.salary.max;
              return {
                min: Math.min(entryMin, entryMax),
                max: Math.max(entryMin, entryMax),
              };
            })(),
            skills:
              entry.skills && entry.skills.length > 0
                ? entry.skills
                : mockEntry.skills,
          };
        })
      : mockData.workExperience;

  const education: EducationEntry[] =
    profileData.education && profileData.education.length > 0
      ? profileData.education.map((entry, index) => {
          const mockEntry = mockData.education?.[index];

          if (!mockEntry) {
            return {
              startDate: entry.startDate || '',
              endDate: entry.endDate || '',
              institution: entry.institution || '',
              degree: entry.degree || '',
              fieldOfStudy: entry.fieldOfStudy,
              description: entry.description,
            };
          }

          return {
            startDate: entry.startDate || mockEntry.startDate,
            endDate: entry.endDate || mockEntry.endDate,
            institution: entry.institution || mockEntry.institution,
            degree: entry.degree || mockEntry.degree,
            fieldOfStudy: entry.fieldOfStudy || mockEntry.fieldOfStudy,
            description: entry.description || mockEntry.description,
          };
        })
      : mockData.education;

  const profileSkillsFiltered = (profileData.skills || []).filter(
    (skill): skill is string =>
      typeof skill === 'string' && skill.trim().length > 0
  );
  const mockSkillsFiltered = (mockData.skills || []).filter(
    (skill): skill is string =>
      typeof skill === 'string' && skill.trim().length > 0
  );
  const combinedSkills = [...profileSkillsFiltered, ...mockSkillsFiltered];
  const skillsSet = new Set(combinedSkills);
  const allSkills = Array.from(skillsSet);

  const publications: PublicationEntry[] =
    profileData.publications && profileData.publications.length > 0
      ? profileData.publications.map((entry, index) => {
          const mockEntry = mockData.publications?.[index];

          if (!mockEntry) {
            return {
              date: entry.date || '',
              title: entry.title || '',
              source: entry.source || '',
              description: entry.description || '',
            };
          }

          return {
            date: entry.date || mockEntry.date,
            title: entry.title || mockEntry.title,
            source: entry.source || mockEntry.source,
            description: entry.description || mockEntry.description,
          };
        })
      : mockData.publications;

  const languages: LanguageEntry[] =
    profileData.languages && profileData.languages.length > 0
      ? profileData.languages.map((entry, index) => {
          const mockEntry = mockData.languages?.[index];

          if (!mockEntry) {
            return {
              language: entry.language || '',
              proficiency: entry.proficiency || '',
            };
          }

          return {
            language: entry.language || mockEntry.language,
            proficiency: entry.proficiency || mockEntry.proficiency,
          };
        })
      : mockData.languages;

  return {
    header,
    metadata,
    workExperience,
    education,
    skills: allSkills,
    publications,
    languages,
  };
};
